import { CRED_STORAGE_FILE, CRED_STORAGE_KEYRING } from "./cli_config.js";
import { isOwnedCredentialPath } from "./paths.js";
import { profilesFilePath } from "./profiles.js";
import { credentialLabel, inspectCredentials, parseCredentialsJson, readCredentials, writeCredentials } from "./credentials.js";
import { existsSync, rmSync } from "node:fs";
import { dirname } from "node:path";
import { createHash } from "node:crypto";
//#region src/credential_store.ts
const KEYRING_SERVICE = "com.neon.neon-cli";
/** Hashing the resolved profiles directory isolates config roots while keeping profile names visible. */
const keyringAccount = (configDir, profile) => `cli:${createHash("sha256").update(dirname(profilesFilePath(configDir))).digest("hex")}:${profile}`;
var KeyringUnavailableError = class extends Error {
	constructor(profile, kind = "read") {
		const loaded = "This CLI cannot use the OS keyring.";
		super(profile === void 0 ? `${loaded} Drop \`--keyring\` to keep the credential in a file.` : kind === "write" ? `${loaded} Remove the profile with \`neon profile remove ${profile} --yes\`.` : `${loaded} Use --api-key or NEON_API_KEY. If this is a standalone neon binary, use the npm-installed neon instead. To reset the profile: \`neon profile remove ${profile} --yes\`.`);
		this.name = "KeyringUnavailableError";
	}
};
var KeyringUnreadableError = class extends Error {
	constructor(profile) {
		const replace = `\`neon auth --profile ${profile}\``;
		super(`Could not read the OS keyring item for profile "${profile}". Unlock the keyring and retry, or run ${replace}. To reset the profile: \`neon profile remove ${profile} --yes\`.`);
		this.name = "KeyringUnreadableError";
	}
};
var KeyringClearError = class extends Error {
	constructor(profile, kind = "visible") {
		const recovery = `\`neon profile remove ${profile} --yes\``;
		super(kind === "unconfirmed" ? `Could not confirm the OS keyring item for profile "${profile}" is gone. The OS store does not distinguish a missing item from denied access. Unlock the OS keyring and retry, or reset the profile with ${recovery} (a leftover may remain; it is unused once the profile is gone).` : `Could not clear the OS keyring item for profile "${profile}". Unlock the OS keyring and retry, or reset the profile with ${recovery} (a leftover may remain; it is unused once the profile is gone).`);
		this.name = "KeyringClearError";
	}
};
const deleteFileIfPresent = (path) => {
	if (!existsSync(path)) return false;
	rmSync(path);
	return true;
};
const inspectKeyringItem = (keyring, account, label) => {
	if (keyring === null) return { kind: "absent" };
	let raw;
	try {
		raw = keyring.get(KEYRING_SERVICE, account);
	} catch {
		return { kind: "absent" };
	}
	if (raw === null) return { kind: "absent" };
	return parseCredentialsJson(raw, label);
};
const createCredentialStore = (dir, options = {}) => {
	const keyring = options.keyring ?? null;
	const accountFor = (profile) => keyringAccount(dir, profile);
	const assertKeyringWritable = (profile) => {
		if (keyring === null) throw new KeyringUnavailableError(profile, "write");
	};
	const setKeyringOrRollback = (profile, credentials) => {
		assertKeyringWritable(profile);
		const kr = keyring;
		if (kr === null) throw new KeyringUnavailableError(profile, "write");
		const account = accountFor(profile);
		const label = `profile "${profile}"`;
		let previous = null;
		try {
			previous = kr.get(KEYRING_SERVICE, account);
		} catch {
			previous = null;
		}
		try {
			kr.set(KEYRING_SERVICE, account, JSON.stringify(credentials));
		} catch {
			throw new KeyringUnavailableError();
		}
		try {
			if (kr.get("com.neon.neon-cli", account) === null) throw new Error(`Wrote credentials to the OS keyring for ${label} but could not read them back.`);
		} catch (err) {
			if (previous !== null) {
				try {
					kr.set(KEYRING_SERVICE, account, previous);
				} catch {
					throw new KeyringClearError(profile, "visible");
				}
				let restored = null;
				try {
					restored = kr.get(KEYRING_SERVICE, account);
				} catch {
					restored = null;
				}
				if (restored === null) throw new KeyringClearError(profile, "visible");
			}
			throw err instanceof Error ? err : new Error(String(err));
		}
	};
	const removeKeyringItem = (profile, required, account = accountFor(profile)) => {
		if (keyring === null) {
			if (required) throw new KeyringUnavailableError(profile, "write");
			return "unconfirmed";
		}
		let raw;
		try {
			raw = keyring.get(KEYRING_SERVICE, account);
		} catch (err) {
			if (!required) return "unconfirmed";
			throw err instanceof Error ? err : new Error(String(err));
		}
		if (raw === null) {
			if (required) throw new KeyringClearError(profile, "unconfirmed");
			return "unconfirmed";
		}
		let deleted;
		try {
			deleted = keyring.delete(KEYRING_SERVICE, account);
		} catch (err) {
			if (!required) return "unconfirmed";
			throw err instanceof Error ? err : new Error(String(err));
		}
		let still;
		try {
			still = keyring.get(KEYRING_SERVICE, account);
		} catch (err) {
			if (!required) return "unconfirmed";
			throw err instanceof Error ? err : new Error(String(err));
		}
		if (!deleted || still !== null) {
			if (required) throw new KeyringClearError(profile, "visible");
			return "left";
		}
		return "cleared";
	};
	const inspect = (at) => {
		if (at.storage === "keyring") {
			if (keyring === null) return {
				file: "unreadable",
				storage: CRED_STORAGE_KEYRING,
				credentials: null,
				reason: new KeyringUnavailableError(at.profile).message
			};
			const keyringRead = inspectKeyringItem(keyring, accountFor(at.profile), credentialLabel(at));
			if (keyringRead.kind === "ok") return {
				file: "ok",
				storage: CRED_STORAGE_KEYRING,
				credentials: keyringRead.credentials
			};
			if (keyringRead.kind === "unusable") return {
				file: "unreadable",
				storage: CRED_STORAGE_KEYRING,
				credentials: null,
				reason: keyringRead.reason
			};
			return {
				file: "unreadable",
				storage: CRED_STORAGE_KEYRING,
				credentials: null,
				reason: new KeyringUnreadableError(at.profile).message
			};
		}
		const fileRead = inspectCredentials(at.path);
		return {
			file: fileRead.kind === "ok" ? "ok" : fileRead.kind === "absent" ? "missing" : "invalid",
			storage: CRED_STORAGE_FILE,
			credentials: fileRead.kind === "ok" ? fileRead.credentials : null,
			...fileRead.kind === "unusable" ? { reason: fileRead.reason } : {}
		};
	};
	const read = (at) => {
		if (at.storage === "keyring") {
			if (keyring === null) throw new KeyringUnavailableError(at.profile);
			let raw;
			try {
				raw = keyring.get(KEYRING_SERVICE, accountFor(at.profile));
			} catch {
				throw new KeyringUnreadableError(at.profile);
			}
			if (raw === null) throw new KeyringUnreadableError(at.profile);
			const parsed = parseCredentialsJson(raw, credentialLabel(at));
			if (parsed.kind === "unusable") throw new Error(parsed.reason);
			if (parsed.kind !== "ok") throw new KeyringUnreadableError(at.profile);
			return {
				credentials: parsed.credentials,
				backend: CRED_STORAGE_KEYRING,
				profile: at.profile
			};
		}
		const credentials = readCredentials(at);
		if (credentials === null) return null;
		return {
			credentials,
			backend: CRED_STORAGE_FILE,
			path: at.path,
			profile: at.profile
		};
	};
	const write = (at, credentials) => {
		if (at.storage === "keyring") {
			setKeyringOrRollback(at.profile, credentials);
			return {
				credentials,
				backend: CRED_STORAGE_KEYRING,
				profile: at.profile
			};
		}
		writeCredentials(at.path, credentials);
		return {
			credentials,
			backend: CRED_STORAGE_FILE,
			path: at.path,
			profile: at.profile
		};
	};
	const del = (at, deleteOptions) => {
		const required = deleteOptions?.required !== false;
		if (at.storage === "keyring") return removeKeyringItem(at.profile, required, deleteOptions?.account);
		if (!isOwnedCredentialPath(dir, at.path)) return "skipped";
		return deleteFileIfPresent(at.path) ? "cleared" : "absent";
	};
	return {
		inspect,
		read,
		write,
		delete: del,
		assertKeyringWritable
	};
};
//#endregion
export { KEYRING_SERVICE, KeyringClearError, KeyringUnavailableError, KeyringUnreadableError, createCredentialStore, keyringAccount };

//# sourceMappingURL=credential_store.js.map